import React, { useEffect } from 'react';
import "@/App.css";
import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import ImpersonationBanner from './components/common/ImpersonationBanner';

// Suppress ResizeObserver loop error (benign browser warning)
if (typeof window !== 'undefined') {
  const originalError = window.console.error;
  window.console.error = (...args) => {
    if (args[0]?.includes?.('ResizeObserver loop') || 
        (typeof args[0] === 'string' && args[0].includes('ResizeObserver'))) {
      return;
    }
    originalError.apply(console, args);
  };
  
  // Suppress from error overlay
  window.addEventListener('error', (e) => {
    if (e.message?.includes('ResizeObserver loop')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, true);
}

// Auth pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import AuthCallback from './pages/auth/AuthCallback';
import SelectPlan from './pages/auth/SelectPlan';

// Dashboard & Main pages
import { DashboardLayout } from './components/layout/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientProfile from './pages/ClientProfile';
import Leads from './pages/Leads';
import Cases from './pages/Cases';
import CaseDetails from './pages/CaseDetails';
import Documents from './pages/Documents';
import Tasks from './pages/Tasks';
import CalendarPage from './pages/Calendar';
import TimeBilling from './pages/TimeBilling';
import Invoices from './pages/Invoices';
import TrustAccounting from './pages/TrustAccounting';
import Messages from './pages/Messages';
import AIAssistant from './pages/AIAssistant';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import KPIDashboard from './pages/KPIDashboard';
import Team from './pages/Team';
import TeamJoin from './pages/TeamJoin';
import CaseHealthDashboard from './pages/CaseHealthDashboard';
import Communication from './pages/Communication';
import FinancialDashboard from './pages/FinancialDashboard';
import EmailTemplates from './pages/EmailTemplates';
import Expenses from './pages/Expenses';
import AuditLogs from './pages/AuditLogs';
import Reminders from './pages/Reminders';
import CaseTemplates from './pages/CaseTemplates';
import DocumentTemplates from './pages/DocumentTemplates';
import CustomFields from './pages/CustomFields';
import ConflictSearch from './pages/ConflictSearch';
import ESignature from './pages/ESignature';
import FirmSettings from './pages/FirmSettings';
import Forms from './pages/Forms';
import Notifications from './pages/Notifications';
import NotificationSettings from './pages/NotificationSettings';
import AdminConsole from './pages/AdminConsole';
import TeamChat from './pages/TeamChat';
import SystemDocumentation from './pages/SystemDocumentation';
import DataRetentionSettings from './pages/DataRetentionSettings';
import NotificationCenter from './pages/NotificationCenter';

// Client Portal pages
import PortalLogin from './pages/portal/PortalLogin';
import PortalSetup from './pages/portal/PortalSetup';
import PortalDashboard from './pages/portal/PortalDashboard';

// Public pages
import IntakeForm from './pages/public/IntakeForm';

// Loading component
import { PageLoader } from './components/common/LoadingSpinner';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Check for impersonation mode
  const isImpersonating = localStorage.getItem('impersonation_session') !== null;
  const impersonationToken = localStorage.getItem('impersonation_token');

  if (loading) {
    return <PageLoader text="Loading..." />;
  }

  // Allow access if impersonating (token is already set)
  if (isImpersonating && impersonationToken) {
    return (
      <>
        <ImpersonationBanner />
        <div style={{ paddingTop: '48px' }}>
          <DashboardLayout>{children}</DashboardLayout>
        </div>
      </>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <DashboardLayout>{children}</DashboardLayout>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader text="Loading..." />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Handle OAuth callback - check for session_id in hash BEFORE routing
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Auth Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Select Plan - Standalone page (no dashboard wrapper) */}
      <Route path="/select-plan" element={<SelectPlan />} />

      {/* Public Intake Form */}
      <Route path="/intake" element={<IntakeForm />} />

      {/* Client Portal Routes */}
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/setup" element={<PortalSetup />} />
      <Route path="/portal" element={<PortalDashboard />} />

      {/* Team Join (public with token) */}
      <Route path="/team/join" element={<TeamJoin />} />

      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
      <Route path="/client/:clientId" element={<ProtectedRoute><ClientProfile /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
      <Route path="/cases" element={<ProtectedRoute><Cases /></ProtectedRoute>} />
      <Route path="/case/:caseId" element={<ProtectedRoute><CaseDetails /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/time-billing" element={<ProtectedRoute><TimeBilling /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/trust" element={<ProtectedRoute><TrustAccounting /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/kpi" element={<ProtectedRoute><KPIDashboard /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
      <Route path="/case-health" element={<ProtectedRoute><CaseHealthDashboard /></ProtectedRoute>} />
      <Route path="/email" element={<ProtectedRoute><Communication /></ProtectedRoute>} />
      <Route path="/email-templates" element={<ProtectedRoute><EmailTemplates /></ProtectedRoute>} />
      <Route path="/financials" element={<ProtectedRoute><FinancialDashboard /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
      <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
      <Route path="/case-templates" element={<ProtectedRoute><CaseTemplates /></ProtectedRoute>} />
      <Route path="/document-templates" element={<ProtectedRoute><DocumentTemplates /></ProtectedRoute>} />
      <Route path="/custom-fields" element={<ProtectedRoute><CustomFields /></ProtectedRoute>} />
      <Route path="/conflict-search" element={<ProtectedRoute><ConflictSearch /></ProtectedRoute>} />
      <Route path="/esignature" element={<ProtectedRoute><ESignature /></ProtectedRoute>} />

      {/* Payment Routes */}
      <Route path="/payment/success" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      
      {/* Firm Settings */}
      <Route path="/firm-settings" element={<ProtectedRoute><FirmSettings /></ProtectedRoute>} />
      
      {/* Legal Forms */}
      <Route path="/forms" element={<ProtectedRoute><Forms /></ProtectedRoute>} />
      
      {/* Notifications */}
      <Route path="/notifications" element={<ProtectedRoute><NotificationCenter /></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
      <Route path="/settings/data-retention" element={<ProtectedRoute><DataRetentionSettings /></ProtectedRoute>} />
      
      {/* Team Chat */}
      <Route path="/team-chat" element={<ProtectedRoute><TeamChat /></ProtectedRoute>} />
      
      {/* Platform Admin Console */}
      <Route path="/admin" element={<AdminConsole />} />
      
      {/* System Documentation - Admin Only */}
      <Route path="/admin/docs" element={<SystemDocumentation />} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <TooltipProvider>
            <div className="App">
              <AppRouter />
              <Toaster position="top-right" />
            </div>
          </TooltipProvider>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
